import { Draft, produce } from 'immer';
import { useCallback, useState } from 'react';

/**
 * An updater function that allows mutation of the `draftState` from immer which
 * will then produce a new immutable state.
 */
export type StateUpdater<T> = (draftState: Draft<T>) => void | Draft<T>;

/**
 * The `setState` function that uses immer's `produce` behind the scenes.
 */
export type SetImmerState<T> = (updater: StateUpdater<T>) => void;

/**
 * A custom `useState` hook that uses immer's `produce` behind the scenes.
 *
 * @example
 * const [userState, setUserState] = useImmerState({ name: 'Jyn', rank: 'Rebel' });
 * ...
 * ...
 *
 * // update state by setting the draft and mutating it directly.
 * // the resulting state will be a new immutable object.
 * setUserState(draft => {
 *   // mutate draft directly
 *   draft.name = 'Jyn Erso';
 *   draft.rank = 'Rogue One Leader';
 * });
 *
 * // alternatively, you can return a new state in the updater function
 * setUserState(_ => {
 *   return { name: 'Cassian Andor' };
 * });
 *
 * @param intialValue The initial value of the state.
 */
export function useImmerState<T>(intialValue: T): [T, SetImmerState<T>] {
  const [internalState, setInternalState] = useState(intialValue);

  const setState = useCallback((updater: StateUpdater<T>) => {
    setInternalState(produce(updater));
  }, []);

  return [internalState, setState];
}
